import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Target, CheckCircle, Star } from "lucide-react";
import { EnhancedPlanRenderer } from "./PlanContentRenderer";

export function PlanDetailDialog({
  plan, onClose,
  activeWeek, onSetActiveWeek,
  onMarkDayComplete
}) {
  if (!plan) return null;

  const completedCount = plan.completed_days?.length || 0;
  const totalDays = plan.duration_weeks * 7;
  const progressPct = Math.round((completedCount / totalDays) * 100);

  return (
    <Dialog open={!!plan} onOpenChange={() => onClose()}>
      <DialogContent data-testid="view-plan-dialog" className="max-w-7xl h-[90vh] flex flex-col overflow-hidden bg-gradient-to-br from-white to-emerald-50 mx-2 sm:mx-auto rounded-xl">
        <DialogHeader className="flex-shrink-0 border-b border-emerald-100 pb-4 px-3 sm:px-6">
          <div>
            <DialogTitle className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-2">
              {plan.title}
            </DialogTitle>
            <DialogDescription className="text-sm sm:text-base text-gray-700">
              {plan.description}
            </DialogDescription>
          </div>

          {/* Progress Summary */}
          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-4 text-sm flex-wrap">
              <div className="flex items-center gap-2 bg-emerald-100 px-3 py-1 rounded-full">
                <Target className="w-4 h-4 text-emerald-700" />
                <span className="font-medium text-emerald-900">{plan.duration_weeks} weeks &middot; 7 days each</span>
              </div>
              <div className="flex items-center gap-2 bg-blue-100 px-3 py-1 rounded-full">
                <Star className="w-4 h-4 text-blue-700" />
                <span className="font-medium text-blue-900">{progressPct}% complete</span>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden shadow-inner">
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500 ease-out rounded-full"
                style={{ width: `${progressPct}%` }}
              />
            </div>

            {/* Week Navigator Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-emerald-300">
              {Array.from({ length: plan.duration_weeks }, (_, i) => i + 1).map(weekNum => {
                const completedDaysInWeek = (plan.completed_days || []).filter(
                  day => day > (weekNum - 1) * 7 && day <= weekNum * 7
                ).length;
                const isComplete = completedDaysInWeek === 7;
                const isActive = weekNum === activeWeek;

                return (
                  <button
                    key={weekNum}
                    data-testid={`week-tab-${weekNum}`}
                    onClick={() => onSetActiveWeek(weekNum)}
                    className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? isComplete
                          ? 'bg-emerald-600 text-white shadow-md ring-2 ring-emerald-300'
                          : 'bg-emerald-500 text-white shadow-md ring-2 ring-emerald-200'
                        : isComplete
                        ? 'bg-emerald-100 text-emerald-800 border-2 border-emerald-400 hover:bg-emerald-200'
                        : completedDaysInWeek > 0
                        ? 'bg-amber-100 text-amber-900 border-2 border-amber-400 hover:bg-amber-200'
                        : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-emerald-400 hover:text-emerald-700'
                    }`}
                  >
                    <span className="flex items-center gap-1">
                      Week {weekNum}
                      {isComplete && <CheckCircle className="w-3 h-3" />}
                      <span className="text-xs">({completedDaysInWeek}/7)</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </DialogHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* How to Use - only show when on week 1 */}
          {activeWeek === 1 && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-5 rounded-r-lg mb-4">
              <h3 className="text-lg font-bold text-blue-900 mb-3">How to Use Your Training Plan</h3>
              <div className="space-y-2 text-sm text-gray-800">
                {[
                  [<><strong>Switch Weeks</strong> using the tabs above to view each week's plan</>],
                  [<><strong>Review Week Overview</strong> - Each week shows detailed objectives, focus areas, and goals</>],
                  [<><strong>Follow Daily Routines</strong> - Each day includes specific exercises, techniques, and tasks</>],
                  [<><strong>Check the box</strong> next to each day when you finish all activities - <span className="text-emerald-600 font-semibold">This updates your Training Plan Adherence metrics!</span></>],
                  [<><strong>Track your progress</strong> - Monitor completion percentage and stay consistent!</>],
                ].map((content, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold mt-0.5">{i + 1}.</span>
                    <p>{content[0]}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-blue-200">
                <div className="px-3 py-2 bg-emerald-50 border border-emerald-300 rounded text-xs text-emerald-900">
                  <strong>Adherence Tracking:</strong> Checking off days in THIS section updates your Training Plan Adherence on the Mental Performance page.
                </div>
              </div>
            </div>
          )}

          {/* Plan Content - only active week */}
          <EnhancedPlanRenderer
            plan={plan}
            activeWeek={activeWeek}
            onMarkDayComplete={onMarkDayComplete}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
